/**
 * useExchangeRate — single source of truth for USD→MYR conversion.
 *
 * Fetches admin-set rates from `/api/exchange-rates` (per YYYY-MM) so
 * Channel Overview, Channel Performance, Scorecard, and Daily Data all
 * multiply USD × the SAME rate for a given month. Backend uses the same
 * source via `get_rate_for_period` in utils/data_helpers.py.
 *
 * Usage:
 *   const { getRateForDate, defaultRate, ratesMap } = useExchangeRate();
 *   const commissionMyr = row.brokers[bk].commission_usd * getRateForDate(row.date);
 */
import { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_RATE_BY_YEAR = { 2025: 4.4, 2026: 4.2 };
const FALLBACK_RATE = 4.2;

function pickDefault(dateStr) {
  const s = dateStr instanceof Date ? dateStr.toISOString() : String(dateStr || '');
  const y = parseInt(s.substring(0, 4), 10);
  return DEFAULT_RATE_BY_YEAR[y] || FALLBACK_RATE;
}

/** Convert a Date | string | undefined to 'YYYY-MM'. Returns '' if invalid. */
export function toYearMonth(d) {
  if (!d) return '';
  if (d instanceof Date && !isNaN(d)) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }
  const s = String(d);
  if (s.length >= 7 && s[4] === '-') return s.substring(0, 7);
  return '';
}

export function useExchangeRate() {
  const [ratesMap, setRatesMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/exchange-rates`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : [])
      .then((rows) => {
        const map = {};
        (rows || []).forEach((r) => {
          if (r && r.year && r.month && r.rate) {
            const key = `${r.year}-${String(r.month).padStart(2, '0')}`;
            map[key] = Number(r.rate);
          }
        });
        setRatesMap(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getRateForDate = useCallback((dateStr) => {
    const key = dateStr instanceof Date
      ? `${dateStr.getFullYear()}-${String(dateStr.getMonth() + 1).padStart(2, '0')}`
      : String(dateStr || '').substring(0, 7);
    if (ratesMap[key]) return ratesMap[key];
    return pickDefault(dateStr);
  }, [ratesMap]);

  // Rate for the "current" month (used for aggregate displays that don't
  // know per-row dates, e.g. daily average of an aggregated total).
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const defaultRate = ratesMap[currentKey] || pickDefault(now.toISOString());

  return { ratesMap, getRateForDate, defaultRate, loading };
}

export default useExchangeRate;
